import dot from 'dot-object'

/**
 * Evaluate a condition object against a values lookup.
 *
 * New format — any/all nesting:
 *   { any: [ conditionA, conditionB ] }         // OR
 *   { all: [ conditionA, conditionB ] }         // AND
 *   { field, operator, value }                  // leaf check
 *
 * Old format — backward compat with checks/operators:
 *   { checks: [...], operators: [[0,1],[2]] }
 *
 * @param {Object} condition
 * @param {Function} lookup  (path: string) => value
 */
export function evaluateCondition(condition, lookup) {
  if (!condition) return true

  if (condition.any) return condition.any.some(c => evaluateCondition(c, lookup))
  if (condition.all) return condition.all.every(c => evaluateCondition(c, lookup))

  // Old format: checks array + operators groups
  if (condition.checks && condition.operators) {
    const results = condition.checks.map(check => evalLeaf(check, lookup))
    return condition.operators.some(group => group.every(idx => results[idx]))
  }

  // Leaf check: { field, operator, value }
  return evalLeaf(condition, lookup)
}

function evalLeaf(check, lookup) {
  const val = lookup(check.field)
  if (check.operator === 'EQUALS')     return val === check.value
  if (check.operator === 'NOT_EQUALS') return val !== check.value
  return false
}

/**
 * Convenience: build a lookup from one values object with an optional fallback.
 * Values takes precedence over fallback (mirrors the original ?? pattern).
 */
export function valuesLookup(values, fallback = null) {
  return path => dot.pick(path, values) ?? (fallback ? dot.pick(path, fallback) : undefined)
}
